import { useEffect, useState } from 'react';

import { ChevronLeft, ChevronRight } from '@gympass/yoga-icons';
import {
  selectedDateAtom,
  selectedMonthAtom,
  selectedYearAtom,
} from 'atoms/dateSelector';
import { useAtom, useAtomValue } from 'jotai';
import { useIntl } from 'react-intl';
import { addMonthToDate, formatToMonthAndYear } from 'utils';

import { DateSelectorProps } from './types';

import * as S from './styles';

const DateSelector: React.FC<DateSelectorProps> = ({
  minDate,
  maxDate,
  onDateSelection,
  selectionCoverage = 'month',
}) => {
  const intl = useIntl();
  const [selectedDate, setSelectedDate] = useAtom(selectedDateAtom);

  const selectedYear = useAtomValue(selectedYearAtom);
  const selectedMonth = useAtomValue(selectedMonthAtom);

  const [initialDate] = useState(selectedDate);

  const isMinAvailableDate = () => {
    if (minDate) {
      const minYear = minDate.getFullYear();
      const minMonth = minDate.getMonth() + 1;

      return (
        selectedYear < minYear ||
        (selectedYear === minYear && selectedMonth <= minMonth)
      );
    }
    return false;
  };

  const isMaxAvailableDate = () => {
    if (maxDate) {
      const maxYear = maxDate.getFullYear();
      const maxMonth = maxDate.getMonth() + 1;

      return (
        selectedYear > maxYear ||
        (selectedYear === maxYear && selectedMonth >= maxMonth)
      );
    }
    return false;
  };

  const handleSelectPreviousDate = () => {
    if (selectionCoverage === 'month' && !isMinAvailableDate()) {
      setSelectedDate(addMonthToDate(selectedDate, -1));
    }
  };

  const handleSelectNextDate = () => {
    if (selectionCoverage === 'month' && !isMaxAvailableDate()) {
      setSelectedDate(addMonthToDate(selectedDate, 1));
    }
  };

  useEffect(() => {
    if (selectedDate !== initialDate) {
      onDateSelection();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  return (
    <S.Container data-testid="date-selector">
      <S.ButtonIcon
        inverted
        secondary
        icon={ChevronLeft}
        hide={isMinAvailableDate()}
        onClick={handleSelectPreviousDate}
        title={`previous-${selectionCoverage}`}
        aria-label={`previous-${selectionCoverage}`}
        data-testid="date-selection-previous-button"
      />

      <S.Date data-testid="date-selection-info">
        {formatToMonthAndYear(selectedDate, { locale: intl.locale })}
      </S.Date>

      <S.ButtonIcon
        inverted
        secondary
        icon={ChevronRight}
        hide={isMaxAvailableDate()}
        onClick={handleSelectNextDate}
        title={`next-${selectionCoverage}`}
        aria-label={`next-${selectionCoverage}`}
        data-testid="date-selection-next-button"
      />
    </S.Container>
  );
};

export default DateSelector;
